import React, {useContext, useEffect} from 'react'
import { Routes, Route, Navigate } from 'react-router-dom';
import MyContext from "./Context";
import Machines from './screens/machines/machines';
import Login from './screens/login/login/login';
import Operations from './screens/operations/sandbox/operations';
import Register from './screens/register';
import OperationGroups from "./screens/operations/groups/operation_groups";
import SingleGraphic from "./screens/graphics/graphics_test";
import Hub from "./screens/hub/hub";
import DeviceList from "./screens/devices_table/devices";
import Variables from "./screens/variables/variables";
import GeneralSettings from "./screens/settings/general/general";
import CompanySettings from "./screens/settings/companies/companies";
import PaymentSettings from "./screens/settings/payments/payments";
import UserSettings from "./screens/settings/users/users";
import RoleSettings from "./screens/settings/roles/roles";
import DevicesBoard from "./screens/devices/devices";
import Offices from "./screens/offices/offices";
import AdminDashboard from "./screens/admin/dashboard/dashboard";
import OperationsHistory from "./screens/operations/history/history";
import ForgotPassword from "./screens/login/forgot/forgot";
import ListDeviceTypes from "./screens/admin/device_types/list";
import ListDeviceTypeVariables from "./screens/admin/device_types/variables/list";
import MQTTConfig from "./screens/admin/device_types/mqtt_config";
import Dashboard from "./screens/dashboard/dashboard";
import SetNewPassword from "./screens/login/forgot/setnew";
import NewPaymentSettings from "./screens/settings/payments/new_payments";
import PaymentPlans from "./screens/admin/payment_plans/list";
import AdminSubscriptions from "./screens/admin/subscriptions/subscriptions";
import HistoricList from "./screens/historic/list";
import Reports from "./screens/reports/reports";
import Onboarding from "./screens/onboarding/onboarding";
import Pricing from "./screens/pricing/pricing";
import Connections from "./screens/connections/connections";
import Cameras from "./screens/cameras/cameras";
import GPSMap from "./screens/gps/gps_map";
import GPSAssets from "./screens/gps/gps_assets";
import GPSZones from "./screens/gps/gps_zones";
import GPSRules from "./screens/gps/gps_rules";
import GPSActions from "./screens/gps/gps_actions";
import HistoricalData from "./screens/historical/historical_data";
import ProductionComparison from "./screens/production/production_comparison";
import MaintenanceDashboard from "./screens/maintenance/maintenance_dashboard";
import SCADAList from './screens/scada/scada_list';
import SCADAEditor from './screens/scada/scada_editor';

function LoginRedirect() {
    useEffect(() => {
        window.location.replace('/login');
    }, []);
    return (<div></div>);
}

const SIDEBAR_ROUTES = [
    { path: '/resources', module: 'assets' },
    { path: '/dashboards', module: 'dashboards' },
    { path: '/gps', module: 'gps' },
    { path: '/operations', module: 'operations' },
    { path: '/device_list', module: 'devices' },
    { path: '/historical-data', module: 'historical_data' },
    { path: '/production/comparison', module: 'production' },
    { path: '/connections', module: 'connections' },
    { path: '/cameras', module: 'cameras' },
    { path: '/reports', module: 'reports' },
    { path: '/maintenance', module: 'maintenance' },
    { path: '/scada', module: 'scada' },
];

function getDefaultRoute(user) {
    if (user?.is_superuser) return '/resources';
    const permissions = user?.role?.permissions;
    if (!permissions) return '/resources';
    for (const route of SIDEBAR_ROUTES) {
        const perm = permissions.find(p => p.module === route.module);
        if (perm?.can_view) return route.path;
    }
    return '/settings/general';
}

function PermRoute({ module, element, feature }) {
    const { user, userLoaded, corporateGroup } = useContext(MyContext);
    if (!userLoaded) return null;
    if (!module) return element;
    if (module === '__superuser__') {
        return user?.is_superuser ? element : <Navigate to="/resources" replace />;
    }
    if (user?.is_superuser) return element;
    const perm = user?.role?.permissions?.find(p => p.module === module);
    if (!perm?.can_view) return <Navigate to={getDefaultRoute(user)} replace />;
    if (feature) {
        const cgLoaded = corporateGroup && Object.keys(corporateGroup).length > 0;
        if (!cgLoaded) return null;
        if (!corporateGroup[feature]) return <Navigate to={getDefaultRoute(user)} replace />;
    }
    return element;
}

export default function RouteContainer() {
    let authenticated = localStorage.getItem('access_token') !== null;
    //let staff = !!Cookies.get('staff');

    return (
        <Routes>
            <Route path='/login' element={<Login/>}>Login</Route>
            <Route path='/forgot' element={<ForgotPassword/>}>Forgot Password</Route>
            <Route path='/reset-password/:token' element={<SetNewPassword/>}>Set New Password</Route>
            <Route path='/register/:token' element={<Register/>}>Register</Route>
            <Route path='/onboarding' element={<Onboarding/>}>Onboarding</Route>
            {authenticated &&
                <>
                    <Route path='/' element={<Hub/>}/>
                    <Route path='/dashboards' element={<PermRoute module="dashboards" element={<Dashboard/>}/>}/>
                    <Route path='/resources' element={<PermRoute module="assets" element={<Machines/>}/>}/>
                    <Route path='/machines' element={<Navigate to="/resources" replace />}/>
                    <Route path='/variables' element={<PermRoute module="assets" element={<Variables/>}/>}/>
                    <Route path='/devices' element={<PermRoute module="assets" element={<DevicesBoard/>}/>}/>
                    <Route path='/offices' element={<PermRoute module="offices" element={<Offices/>}/>}/>
                    <Route path='/device_list' element={<PermRoute module="devices" element={<DeviceList/>}/>}/>
                    <Route path='/operations' element={<PermRoute module="operations" element={<OperationGroups/>}/>}/>
                    <Route path='/operations/show' element={<PermRoute module="operations" element={<Operations/>}/>}/>
                    <Route path='/operations/history' element={<PermRoute module="operations" element={<OperationsHistory/>}/>}/>
                    <Route path='/graphics/show' element={<SingleGraphic/>}/>
                    <Route path='/historic' element={<PermRoute module="historical_data" element={<HistoricList/>}/>}/>
                    <Route path='/reports' element={<PermRoute module="reports" element={<Reports/>}/>}/>
                    <Route path='/connections' element={<PermRoute module="connections" element={<Connections/>}/>}/>
                    <Route path='/cameras' element={<PermRoute module="cameras" element={<Cameras/>}/>}/>
                    <Route path='/historical-data' element={<PermRoute module="historical_data" element={<HistoricalData/>}/>}/>
                    <Route path='/gps' element={<PermRoute module="gps" feature="allows_gps_tracking" element={<GPSMap/>}/>}/>
                    <Route path='/gps/assets' element={<PermRoute module="gps" feature="allows_gps_tracking" element={<GPSAssets/>}/>}/>
                    <Route path='/gps/zones' element={<PermRoute module="gps" feature="allows_gps_tracking" element={<GPSZones/>}/>}/>
                    <Route path='/gps/rules' element={<PermRoute module="gps" feature="allows_gps_tracking" element={<GPSRules/>}/>}/>
                    <Route path='/gps/actions' element={<PermRoute module="gps" feature="allows_gps_tracking" element={<GPSActions/>}/>}/>
<Route path='/production/comparison' element={<PermRoute module="production" element={<ProductionComparison/>}/>}/>
                    <Route path='/maintenance' element={<PermRoute module="maintenance" element={<MaintenanceDashboard/>}/>}/>
                    <Route path='/scada' element={<PermRoute module="scada" feature="allows_scada" element={<SCADAList/>}/>}/>
                    <Route path='/scada/:id' element={<PermRoute module="scada" feature="allows_scada" element={<SCADAEditor/>}/>}/>

                    <Route path='/settings/general' element={<GeneralSettings/>}/>
                    <Route path='/settings/payments' element={<PermRoute module="payments" element={<PaymentSettings/>}/>}/>
                    <Route path='/settings/companies' element={<PermRoute module="companies" element={<CompanySettings/>}/>}/>
                    <Route path='/settings/users' element={<PermRoute module="users" element={<UserSettings/>}/>}/>
                    <Route path='/settings/roles' element={<PermRoute module="roles" element={<RoleSettings/>}/>}/>
                    <Route path='/pricing' element={<Pricing/>}/>

                    <Route path='/admin' element={<PermRoute module="__superuser__" element={<AdminDashboard/>}/>}/>
                    <Route path='/admin/device_types' element={<PermRoute module="__superuser__" element={<ListDeviceTypes/>}/>}/>
                    <Route path='/admin/device_types/mqtt_config' element={<PermRoute module="__superuser__" element={<MQTTConfig/>}/>}/>
                    <Route path='/admin/device_types/variables' element={<PermRoute module="__superuser__" element={<ListDeviceTypeVariables/>}/>}/>
                    <Route path='/admin/payment_plans' element={<PermRoute module="__superuser__" element={<PaymentPlans/>}/>}/>
                    <Route path='/admin/subscriptions' element={<PermRoute module="__superuser__" element={<AdminSubscriptions/>}/>}/>
                </>
            }

            <Route path="*" element={<LoginRedirect/>}/>
        </Routes>
    );
}
